import { cn, Mono, Text } from "@athena/ui";
import React, { useState } from "react";
import { ChevronLeft, GripVertical, ChevronDown, ChevronRight, X, Eye, EyeOff } from "lucide-react";
import { CONTENT_TYPE_META } from "./CourseHelpers";
import type { Module } from "./CoursePreviewPage";

interface CourseOutlineProps {
    sidebarCollapsed: boolean;
    setSidebarCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
    modules: Module[];
    highlightSection: (id: string) => void;
    onToggleVisibility: (id: string) => void;
}

const CourseOutline: React.FC<CourseOutlineProps> = ({
    sidebarCollapsed,
    setSidebarCollapsed,
    modules,
    highlightSection,
    onToggleVisibility,
}) => {
    const [
        expandedModules,
        setExpandedModules,
    ] = useState<Set<string>>(new Set(modules.map((m) => m.id)));

    const toggleModule = (id: string) => {
        setExpandedModules((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    return (
        <div
            className={cn(
                "border-border bg-background flex shrink-0 transition-all duration-200",
                sidebarCollapsed ? "w-4" : "w-65",
            )}
        >
            {/* Content */}
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                {!sidebarCollapsed && (
                    <>
                        <div className="mt-4 flex-1 overflow-y-auto px-2">
                            <div className="mb-2 flex items-center justify-between">
                                <Mono size="xs" muted className="tracking-widest uppercase">
                                    Outline
                                </Mono>
                                <X
                                    size="13"
                                    className="text-text-muted hover:bg-muted hover:cursor-pointer"
                                    onClick={() => setSidebarCollapsed((c) => !c)}
                                />
                            </div>

                            {modules.map((mod, index) => {
                                const modItems = mod.content;
                                return (
                                    <div key={mod.id} className="mb-1">
                                        <div className="flex items-center gap-1">
                                            <GripVertical className="text-muted-foreground/40 h-3.5 w-3.5 shrink-0 cursor-grab" />
                                            <button
                                                onClick={() => toggleModule(mod.id)}
                                                className="hover:bg-muted flex flex-1 items-center gap-1.5 rounded-md px-1.5 py-1.5 text-left transition-colors"
                                            >
                                                <ChevronDown
                                                    className={cn(
                                                        "text-muted-foreground h-3.5 w-3.5 shrink-0 transition-transform",
                                                        !expandedModules.has(mod.id) && "-rotate-90",
                                                    )}
                                                />
                                                <Mono size="xs" muted className="w-5 shrink-0">
                                                    {String(index + 1).padStart(2, "0")}
                                                </Mono>
                                                <Text size="xs" weight="semibold" className="flex-1">
                                                    {mod.title}
                                                </Text>
                                            </button>
                                        </div>

                                        {expandedModules.has(mod.id) && (
                                            <div className="border-border mt-0.5 ml-7 flex flex-col gap-0.5 border-l pl-2">
                                                {modItems.map((item) => {
                                                    const meta = CONTENT_TYPE_META[item.contentType];
                                                    return (
                                                        <div
                                                            key={item.id}
                                                            className="group hover:bg-muted relative flex items-center px-0.5 py-1 hover:rounded-md"
                                                        >
                                                            <button
                                                                onClick={() => highlightSection(item.id)}
                                                                className="flex min-w-0 flex-1 items-center gap-3 text-left"
                                                            >
                                                                <span
                                                                    className={cn(
                                                                        "flex shrink-0 items-center justify-center rounded-lg [&>svg]:h-3 [&>svg]:w-3",
                                                                        meta.color,
                                                                        item.status === "HIDDEN" && "opacity-40",
                                                                    )}
                                                                >
                                                                    {meta.icon}
                                                                </span>
                                                                <div className="min-w-0">
                                                                    <Text
                                                                        size="2xs"
                                                                        className={cn(
                                                                            "truncate",
                                                                            item.status === "HIDDEN" && "opacity-40",
                                                                        )}
                                                                    >
                                                                        {item.title}
                                                                    </Text>
                                                                </div>
                                                            </button>
                                                            <button
                                                                onClick={() => onToggleVisibility(item.id)}
                                                                aria-label={
                                                                    item.status === "HIDDEN" ? "Show item" : "Hide item"
                                                                }
                                                                className={cn(
                                                                    "flex h-5 w-5 shrink-0 items-center justify-center rounded transition-colors",
                                                                    item.status === "HIDDEN"
                                                                        ? "text-destructive"
                                                                        : "text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100",
                                                                )}
                                                            >
                                                                {item.status === "HIDDEN" ? (
                                                                    <EyeOff className="h-3 w-3" />
                                                                ) : (
                                                                    <Eye className="h-3 w-3" />
                                                                )}
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </div>

            {/* Toggle strip */}
            <button
                onClick={() => setSidebarCollapsed((v) => !v)}
                className="text-muted-foreground hover:bg-secondary/10 hover:text-foreground group flex w-6 shrink-0 cursor-pointer items-center justify-center border-r transition-colors"
                aria-label={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            >
                {sidebarCollapsed ? (
                    <ChevronRight className="text-secondary h-6 w-6 transition-colors" />
                ) : (
                    <ChevronLeft className="text-secondary h-6 w-6 transition-colors" />
                )}
            </button>
        </div>
    );
};

export default CourseOutline;
